'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

const DOMAINS = ['mailnova.io', 'dropbox.email', 'fastinbox.net', 'companyinbox.online'];

function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const step = Math.ceil(target / 60);
    const timer = setInterval(() => {
      setCount((c) => {
        if (c + step >= target) { clearInterval(timer); return target; }
        return c + step;
      });
    }, 20);
    return () => clearInterval(timer);
  }, [target]);
  return <>{count.toLocaleString()}{suffix}</>;
}

export default function HomePage() {
  const [activeDomain, setActiveDomain] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setActiveDomain((d) => (d + 1) % DOMAINS.length), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Ambient background orbs — orange brand */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute top-[-10%] left-[-5%] w-[600px] h-[600px] rounded-full opacity-15 animate-float"
          style={{ background: 'radial-gradient(circle, rgba(232,96,10,0.35) 0%, transparent 70%)', filter: 'blur(60px)' }} />
        <div className="absolute top-[30%] right-[-10%] w-[500px] h-[500px] rounded-full opacity-10 animate-float"
          style={{ background: 'radial-gradient(circle, rgba(255,140,66,0.3) 0%, transparent 70%)', filter: 'blur(50px)', animationDelay: '3s' }} />
        <div className="absolute bottom-[10%] left-[20%] w-[400px] h-[400px] rounded-full opacity-8 animate-float"
          style={{ background: 'radial-gradient(circle, rgba(200,70,0,0.35) 0%, transparent 70%)', filter: 'blur(40px)', animationDelay: '6s' }} />
      </div>

      {/* Navigation */}
      <nav className="relative z-30 sticky top-0 glass-topbar border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center gap-6">
          <Link href="/" className="flex items-center gap-2.5 flex-shrink-0">
            <div className="glass rounded-xl p-1.5">
              <AppLogo size={24} />
            </div>
            <span className="font-bold text-lg tracking-tight text-foreground">CompanyInbox</span>
          </Link>

          <div className="hidden md:flex items-center gap-1 ml-4">
            {[
              { label: 'Features', href: '#features' },
              { label: 'SecurePaste', href: '/secure-paste' },
              { label: 'Pricing', href: '/upgrade-pricing-screen' },
              { label: 'Domains', href: '/domains' },
            ].map((item) => (
              <a key={item.label} href={item.href}
                className="px-3 py-2 rounded-lg text-sm font-medium text-secondary-foreground hover:text-foreground hover:bg-white/60 transition-all duration-150">
                {item.label}
              </a>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-3">
            <Link href="/sign-up-login-screen"
              className="hidden sm:block text-sm font-medium text-secondary-foreground hover:text-foreground transition-colors px-3 py-2">
              Sign in
            </Link>
            <Link href="/sign-up-login-screen"
              className="btn-primary text-sm py-2 px-5">
              Get started free
            </Link>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/60 transition-all">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {mobileMenuOpen ? <><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></> : <><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></>}
              </svg>
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden glass-intense border-t border-white/40 px-6 py-4 space-y-1">
            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-white/60">Features</a>
            <Link href="/secure-paste" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-white/60">SecurePaste</Link>
            <Link href="/upgrade-pricing-screen" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-white/60">Pricing</Link>
            <Link href="/domains" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-white/60">Domains</Link>
            <Link href="/sign-up-login-screen" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-white/60">Sign in</Link>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 pt-20 pb-24 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass border border-primary/20 text-xs font-semibold text-primary mb-8 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            No sign-up required · Instant inbox · Privacy-first
          </div>

          <h1 className="text-5xl sm:text-6xl xl:text-7xl font-bold leading-[1.08] tracking-tight text-foreground mb-6 animate-fade-in">
            Professional email,<br />
            <span className="text-gradient-primary">without the baggage.</span>
          </h1>

          <p className="text-xl text-secondary-foreground max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in">
            Generate a disposable inbox in 3 seconds. Use it for signups, testing, or anything you don&apos;t want in your real inbox. Upgrade to keep it forever on your own domain.
          </p>

          {/* Live address preview */}
          <div className="max-w-lg mx-auto mb-10 animate-fade-in">
            <div className="glass-card rounded-2xl p-4 border border-primary/15">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Your instant address</span>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-xl bg-white/70 border border-white/80">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary flex-shrink-0">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
                <span className="font-mono text-sm font-semibold text-foreground flex-1 text-left">
                  xk7-swift@<span className="text-primary transition-all duration-500">{DOMAINS[activeDomain]}</span>
                </span>
                <Link href="/sign-up-login-screen"
                  className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-bold hover:bg-primary/90 transition-all">
                  Use this →
                </Link>
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">Expires in 24 hours · No account needed</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in">
            <Link href="/sign-up-login-screen"
              className="btn-primary px-8 py-3.5 text-base font-semibold w-full sm:w-auto">
              Create free inbox
            </Link>
            <Link href="/upgrade-pricing-screen"
              className="btn-ghost px-8 py-3.5 text-base font-semibold w-full sm:w-auto">
              See pricing →
            </Link>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="relative z-10 py-12 px-6 border-y border-white/30">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-2xl p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { value: 84000, suffix: '+', label: 'Addresses generated' },
                { value: 120, suffix: '+', label: 'Countries served' },
                { value: 99, suffix: '.9%', label: 'Uptime SLA' },
                { value: 3, suffix: 's', label: 'Avg. delivery time' },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-3xl font-bold text-foreground tabular-nums">
                    <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative z-10 py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="badge-permanent w-fit mx-auto mb-4">Built for professionals</div>
            <h2 className="text-4xl font-bold text-foreground mb-4">
              Everything your real inbox can&apos;t do
            </h2>
            <p className="text-lg text-secondary-foreground max-w-xl mx-auto">
              CompanyInbox is built for developers, founders, and privacy-conscious teams who need a real email workflow — without the noise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                  </svg>
                ),
                title: 'Instant inbox, zero friction',
                desc: 'Your address is live in under 3 seconds. No verification, no forms, no waiting. Share it anywhere and messages arrive immediately.',
                badge: 'Free',
              },
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                  </svg>
                ),
                title: 'Bring your own domain',
                desc: 'Connect your company domain via DNS records. Receive email at hello@yourcompany.com through our infrastructure — no hosting required.',
                badge: 'Business',
              },
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                ),
                title: 'Permanent addresses from $4/mo',
                desc: 'Upgrade to Pro and your address never expires. Pick a custom username like alex@mailnova.io and keep it as long as you want.',
                badge: 'Pro',
              },
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <ellipse cx="12" cy="5" rx="9" ry="3" />
                    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                  </svg>
                ),
                title: 'Up to 200 GB cloud storage',
                desc: 'Attachments are stored securely in your cloud drive. Download them any time, search across all your messages, and never lose an important file.',
                badge: 'All plans',
              },
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 18 22 12 16 6" />
                    <polyline points="8 6 2 12 8 18" />
                  </svg>
                ),
                title: 'Full REST API access',
                desc: 'Pipe verification codes directly into your test automation. Read messages, list addresses, and manage your inbox programmatically via our REST API.',
                badge: 'Business',
              },
              {
                icon: (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 00-3-3.87" />
                    <path d="M16 3.13a4 4 0 010 7.75" />
                  </svg>
                ),
                title: 'Team shared inboxes',
                desc: 'Business plan supports up to 10 team seats with a shared inbox. Perfect for support@, billing@, or any team address that multiple people need to monitor.',
                badge: 'Business',
              },
            ].map((feature) => (
              <div key={feature.title} className="card-elevated p-6 hover:border-primary/30 transition-all duration-200 group">
                <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-4 group-hover:bg-primary/15 transition-colors">
                  {feature.icon}
                </div>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="text-base font-bold text-foreground leading-snug">{feature.title}</h3>
                  <span className="flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/8 text-primary border border-primary/15">
                    {feature.badge}
                  </span>
                </div>
                <p className="text-sm text-secondary-foreground leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="relative z-10 py-24 px-6 bg-gradient-to-b from-transparent via-orange-500/3 to-transparent">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">Up and running in 30 seconds</h2>
            <p className="text-lg text-secondary-foreground">No configuration. No DNS records. No credit card. Just an inbox.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Generate your address',
                desc: 'Click "Create free inbox" and your address is ready instantly. Choose from mailnova.io, dropbox.email, or fastinbox.net.',
              },
              {
                step: '02',
                title: 'Share it anywhere',
                desc: 'Use it for signups, API testing, newsletter subscriptions, or any service you don\'t fully trust with your real email.',
              },
              {
                step: '03',
                title: 'Read messages in real time',
                desc: 'Messages arrive in your inbox within seconds. Read them, download attachments, and delete when done. Upgrade to keep it forever.',
              },
            ].map((step, i) => (
              <div key={step.step} className="relative">
                {i < 2 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-px bg-gradient-to-r from-primary/30 to-transparent z-0" style={{ width: 'calc(100% - 2rem)', left: 'calc(100% - 1rem)' }} />
                )}
                <div className="glass-card rounded-2xl p-6 relative z-10">
                  <div className="text-4xl font-black text-primary/20 mb-3 font-mono">{step.step}</div>
                  <h3 className="text-base font-bold text-foreground mb-2">{step.title}</h3>
                  <p className="text-sm text-secondary-foreground leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SecurePaste Feature Section */}
      <section className="relative z-10 py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: copy */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-xs font-bold text-orange-600 mb-6">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                </svg>
                New · SecurePaste
              </div>
              <h2 className="text-4xl font-bold text-foreground mb-4 leading-tight">
                Share secrets that<br />
                <span className="text-gradient-primary">self-destruct.</span>
              </h2>
              <p className="text-lg text-secondary-foreground mb-6 leading-relaxed">
                Drop API keys, deploy scripts, credentials, or any sensitive text into a paste. Get a unique URL. Set it to expire in 1 hour, 24 hours, or never. No account needed to view.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Expiring links — 1h, 24h, 7d, 30d, or never',
                  'Syntax highlighting for 10 languages',
                  'Tied to your inbox session — manage all pastes in one place',
                  'Public or private visibility per paste',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-orange-100 border border-orange-200 flex items-center justify-center flex-shrink-0">
                      <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5L4 7L8 3" stroke="#E8600A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                    <span className="text-sm text-secondary-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <Link href="/secure-paste" className="btn-primary px-7 py-3 text-sm font-semibold inline-flex items-center gap-2">
                Try SecurePaste free
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
                </svg>
              </Link>
            </div>

            {/* Right: mock paste preview */}
            <div className="relative">
              <div className="absolute inset-0 rounded-3xl opacity-20 blur-3xl"
                style={{ background: 'radial-gradient(circle, rgba(232,96,10,0.5) 0%, transparent 70%)' }} />
              <div className="relative glass-card rounded-2xl overflow-hidden">
                {/* Fake topbar */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/50 bg-white/40">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-400/70" />
                    <div className="w-3 h-3 rounded-full bg-amber-400/70" />
                    <div className="w-3 h-3 rounded-full bg-emerald-400/70" />
                  </div>
                  <span className="text-xs font-mono text-muted-foreground">companyinbox.online/p/x7k2m9qr</span>
                  <span className="text-xs text-amber-600 font-semibold">23h 41m remaining</span>
                </div>
                {/* Fake content */}
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold text-foreground">Staging API Keys</span>
                    <span className="text-xs font-mono bg-orange-50 border border-orange-200 text-orange-600 px-2 py-0.5 rounded">bash</span>
                  </div>
                  <pre className="text-xs font-mono text-secondary-foreground leading-relaxed bg-white/50 rounded-xl p-4 overflow-hidden">
{`# Staging environment — expires in 24h
export STRIPE_KEY="sk_test_4eC39HqLyjWDarjtT1zdp7dc"
export SUPABASE_URL="https://xyzabc.supabase.co"
export SUPABASE_ANON="eyJhbGciOiJIUzI1NiIsInR5cCI..."

# Deploy with:
vercel --env staging`}
                  </pre>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-xs text-muted-foreground">312 chars · 6 views</span>
                    <button className="text-xs font-semibold text-primary hover:text-accent transition-colors">Copy content</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="relative z-10 py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">Who uses CompanyInbox?</h2>
            <p className="text-lg text-secondary-foreground">From solo developers to growing teams — here&apos;s how people use it every day.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                persona: 'Developers & QA Engineers',
                emoji: '👨‍💻',
                uses: [
                  'Test email verification flows without polluting your real inbox',
                  'Pipe OTP codes directly into test automation via the API',
                  'Create isolated inboxes for each test environment',
                  'Receive webhook payloads and transactional emails during development',
                ],
              },
              {
                persona: 'Founders & Indie Hackers',
                emoji: '🚀',
                uses: [
                  'Sign up for competitor tools without revealing your identity',
                  'Use a professional address for your product launch on ProductHunt',
                  'Keep your personal inbox clean from trial subscriptions',
                  'Set up a support@ address on your own domain from day one',
                ],
              },
              {
                persona: 'Privacy-Conscious Users',
                emoji: '🔒',
                uses: [
                  'Sign up for services without handing over your real email',
                  'Receive newsletters without tracking your main inbox',
                  'Protect yourself from data breaches and spam campaigns',
                  'Compartmentalise your online identity across services',
                ],
              },
              {
                persona: 'Small Teams & Startups',
                emoji: '🏢',
                uses: [
                  'Create team inboxes like billing@ and support@ on your domain',
                  'Share access with up to 10 team members on Business plan',
                  'Manage all company email addresses from one dashboard',
                  'Apply for a custom domain and go live in under an hour',
                ],
              },
            ].map((uc) => (
              <div key={uc.persona} className="card-elevated p-6">
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-2xl">{uc.emoji}</span>
                  <h3 className="text-base font-bold text-foreground">{uc.persona}</h3>
                </div>
                <ul className="space-y-2.5">
                  {uc.uses.map((use) => (
                    <li key={use} className="flex items-start gap-2.5">
                      <span className="mt-0.5 w-4 h-4 rounded-full bg-primary/10 border border-primary/25 flex items-center justify-center flex-shrink-0">
                        <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                          <path d="M2 5L4 7L8 3" stroke="#E8600A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </span>
                      <span className="text-sm text-secondary-foreground leading-snug">{use}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="relative z-10 py-24 px-6 bg-gradient-to-b from-transparent via-orange-500/3 to-transparent">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-4">Trusted by builders worldwide</h2>
            <p className="text-lg text-secondary-foreground">Real feedback from real users — no cherry-picking.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                name: 'Rohan Sharma',
                role: 'Senior Backend Engineer · Razorpay',
                quote: 'I use CompanyInbox for every third-party API signup. The inbox is instant, the address actually works with strict senders, and Pro paid for itself in the first week by saving me from inbox spam.',
                stars: 5,
                avatar: 'RS',
              },
              {
                name: 'Kavya Reddy',
                role: 'Product Designer · Groww',
                quote: 'The permanent address feature is exactly what I needed for my portfolio contact form. Clean UI, no clutter, and the custom domain selection makes it feel like a real company email.',
                stars: 5,
                avatar: 'KR',
              },
              {
                name: 'Marcus Chen',
                role: 'Indie Developer · Self-employed',
                quote: 'Been on the Business plan for 3 months. The API access lets me pipe verification codes directly into my test automation. Delivery is faster than Mailinator for most senders.',
                stars: 5,
                avatar: 'MC',
              },
            ].map((t) => (
              <div key={t.name} className="card-elevated p-6 flex flex-col gap-4">
                <div className="flex gap-1">
                  {[1,2,3,4,5].map((s) => (
                    <svg key={s} width="14" height="14" viewBox="0 0 24 24" fill="#FFB830" stroke="#FFB830" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  ))}
                </div>
                <p className="text-sm text-secondary-foreground leading-relaxed flex-1">&ldquo;{t.quote}&rdquo;</p>
                <div className="flex items-center gap-3 pt-3 border-t border-border">
                  <div className="w-9 h-9 rounded-full bg-primary/15 border border-primary/25 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing preview */}
      <section className="relative z-10 py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="badge-active w-fit mx-auto mb-4">Simple pricing</div>
          <h2 className="text-4xl font-bold text-foreground mb-4">Start free. Scale when you need to.</h2>
          <p className="text-lg text-secondary-foreground mb-12 max-w-xl mx-auto">
            The free tier is genuinely useful. Upgrade to Pro for $4/mo when you need a permanent address that never expires.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {[
              {
                name: 'Free',
                price: '$0',
                period: 'forever',
                highlight: false,
                features: ['1 temp address', '24-hour expiry', '1 GB storage', 'Basic search'],
              },
              {
                name: 'Pro',
                price: '$4',
                period: '/mo',
                highlight: true,
                badge: 'Most popular',
                features: ['3 permanent addresses', 'Never expires', '25 GB storage', 'Custom username'],
              },
              {
                name: 'Business',
                price: '$16',
                period: '/mo',
                highlight: false,
                features: ['Unlimited addresses', 'Custom domain', '200 GB storage', 'Full API + 10 seats'],
              },
            ].map((plan) => (
              <div key={plan.name} className={`relative rounded-2xl p-6 text-left ${plan.highlight ? 'shine-border shadow-glow-primary' : 'card-elevated'}`}>
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="badge-permanent px-3 py-1 text-xs font-bold shadow-glow-primary">{plan.badge}</span>
                  </div>
                )}
                <h3 className="text-base font-bold text-foreground mb-1">{plan.name}</h3>
                <div className="flex items-end gap-1 mb-4">
                  <span className="text-3xl font-bold text-foreground">{plan.price}</span>
                  <span className="text-sm text-muted-foreground mb-1">{plan.period}</span>
                </div>
                <ul className="space-y-2">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-secondary-foreground">
                      <svg width="14" height="14" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5L4 7L8 3" stroke="#E8600A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <Link href="/upgrade-pricing-screen" className="btn-primary px-8 py-3.5 text-base font-semibold">
            See full pricing & features →
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 py-24 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="glass-card rounded-3xl p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-40" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 rounded-full opacity-20"
              style={{ background: 'radial-gradient(circle, rgba(232,96,10,0.5) 0%, transparent 70%)', filter: 'blur(40px)' }} />
            <div className="relative z-10">
              <h2 className="text-4xl font-bold text-foreground mb-4">
                Your inbox is ready.<br />
                <span className="text-gradient-primary">Start in 3 seconds.</span>
              </h2>
              <p className="text-lg text-secondary-foreground mb-8 max-w-md mx-auto">
                No credit card. No verification. No spam. Just a clean, professional inbox that works.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link href="/sign-up-login-screen" className="btn-primary px-10 py-4 text-base font-semibold w-full sm:w-auto">
                  Create free inbox
                </Link>
                <Link href="/upgrade-pricing-screen" className="btn-ghost px-10 py-4 text-base font-semibold w-full sm:w-auto">
                  View pricing
                </Link>
              </div>
              <p className="text-xs text-muted-foreground mt-6">
                84,000+ addresses generated · 120+ countries · 99.9% uptime
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/30 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            <div className="md:col-span-2">
              <Link href="/" className="flex items-center gap-2.5 mb-4">
                <div className="glass rounded-xl p-1.5">
                  <AppLogo size={22} />
                </div>
                <span className="font-bold text-base tracking-tight text-foreground">CompanyInbox</span>
              </Link>
              <p className="text-sm text-secondary-foreground leading-relaxed max-w-xs">
                Professional temporary and custom domain email. Privacy-first, instant, and built for the way developers actually work.
              </p>
              <div className="flex items-center gap-3 mt-4">
                <a href="https://twitter.com/companyinbox" target="_blank" rel="noopener noreferrer"
                  className="p-2 rounded-lg glass text-muted-foreground hover:text-foreground transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                <a href="https://github.com/companyinbox" target="_blank" rel="noopener noreferrer"
                  className="p-2 rounded-lg glass text-muted-foreground hover:text-foreground transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
                  </svg>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-sm font-bold text-foreground mb-4">Product</h4>
              <ul className="space-y-2.5">
                {[
                  { label: 'Features', href: '#features' },
                  { label: 'SecurePaste', href: '/secure-paste' },
                  { label: 'Pricing', href: '/upgrade-pricing-screen' },
                  { label: 'Custom Domains', href: '/domains' },
                ].map((link) => (
                  <li key={link.label}>
                    <a href={link.href} className="text-sm text-secondary-foreground hover:text-foreground transition-colors">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-bold text-foreground mb-4">Company</h4>
              <ul className="space-y-2.5">
                {[
                  { label: 'Privacy Policy', href: '#' },
                  { label: 'Terms of Service', href: '#' },
                  { label: 'Security', href: '#' },
                  { label: 'Contact', href: 'mailto:hello@companyinbox.online' },
                ].map((link) => (
                  <li key={link.label}>
                    <a href={link.href} className="text-sm text-secondary-foreground hover:text-foreground transition-colors">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="border-t border-white/30 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              &copy; 2026 CompanyInbox. All rights reserved.
            </p>
            <p className="text-xs text-muted-foreground">
              Built with privacy in mind · Hosted on Vercel · Powered by Supabase
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}